import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { User } from '@app/_models';
import { Dealer } from '@app/_models/dealer';
import { ReportSearch } from '@app/_models/reportSearch';
import { AccountService } from '@app/_services';
import { AlertService } from '@app/_services/alert.service';
import { DealerService } from '@app/_services/dealer-service';
import { SearchService } from '@app/_services/search.service';
import { NgbDateStruct } from '@ng-bootstrap/ng-bootstrap/datepicker/ngb-date-struct';
import { AgGridAngular } from 'ag-grid-angular';
import { GridOptions } from 'ag-grid-community';
import { Subject } from 'rxjs';
import { first, takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-payment-payout-report',
  templateUrl: './payment-payout-report.component.html',
  styleUrls: ['./payment-payout-report.component.less']
})
export class PaymentPayoutReportComponent implements OnInit, OnDestroy {
  @ViewChild('aggrid') aggrid: AgGridAngular;
  certificateReportForm: UntypedFormGroup;
  submitted = false;
  issueDateFrom: NgbDateStruct;
  issueDateTo: NgbDateStruct;
  gridOptions: GridOptions;
  loading = false;
  isAdjustedBalancePositive = false;
  gridReady = true;
  reportSearch: ReportSearch;
  issueDateFromSearch: string;
  issueDateToSearch: string;
  reportCertificateData: any[];
  finalReportData: any[];
  currentDate = new Date();
  reportCreated = false;
  dealer: Dealer;
  totalCredits = 0;
  totalDebits = 0;

  user: User;

  private destroy$ = new Subject<void>();

  constructor(
    private formBuilder: UntypedFormBuilder,
    private searchService: SearchService,
    private alertService: AlertService,
    private accountService: AccountService,
    private dealerService: DealerService
  ) { }

  ngOnInit(): void {
    this.user = this.accountService.userValue;
    this.gridOptions = <GridOptions>{};
    this.certificateReportForm = this.formBuilder.group({
      issueDateFrom: [''],
      issueDateTo: [''],
      dealerId: [''],
      balance: [''],
      adjustedBalance: [''],
      openingBalance: [''],
    });

    this.gridOptions.defaultColDef = {
      resizable: true,
    };

    this.gridOptions.columnDefs = [
      { headerName: "Serial No", valueGetter: "node.rowIndex + 1" }
      , { headerName: "Transaction Date", field: "transactionDate", sortable: true, filter: true }
      , { headerName: "Type", field: "transactionType", sortable: true, filter: true }
      , { headerName: "Dealer Id", field: "dealerId", sortable: true, filter: true }
      , { headerName: "Dealer Name", field: "dealerName", sortable: true, filter: true }
      , { headerName: "Description", field: "comments", sortable: true, filter: true }
      , { headerName: "Credited Amount", field: "credit", sortable: true, filter: true }
      , { headerName: "Debited Amount", field: "debit", sortable: true, filter: true }
      , { headerName: "Dealer Balance", field: "runningBalance", sortable: true, filter: true }
    ]
    this.gridOptions.rowData = [];
    this.gridReady = true;

    this.certificateReportForm.get('issueDateFrom').valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(value => {
        if (value != undefined) {
          this.issueDateFromSearch = value.year + '-' + ('0' + value.month).slice(-2) + '-' + ('0' + value.day).slice(-2);
        }
      });

    this.certificateReportForm.get('issueDateTo').valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(value => {
        if (value != undefined) {
          this.issueDateToSearch = value.year + '-' + ('0' + value.month).slice(-2) + '-' + ('0' + value.day).slice(-2);
        }
      });

  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // convenience getter for easy access to form fields
  get f() { return this.certificateReportForm.controls; }

  onSubmit() {
    this.submitted = true;
    this.loading = true;
    this.reportCreated = false;
    if (this.certificateReportForm.invalid) {
      this.loading = false;
      return;
    }

    this.alertService.clear();
    if (this.user.role != "admin" && this.user.role != "dealerOwner") {
      this.alertService.error("Unauthorized access");
      this.submitted = false;
      this.loading = false;
      return;
    }
    this.reportSearch = new ReportSearch();
    if (this.issueDateFromSearch == undefined) {
      this.reportSearch.issueDateFrom = "0001-01-01";
      this.issueDateFromSearch = this.currentDate.getFullYear() + "-" + ('0' + (this.currentDate.getMonth() + 1)).slice(-2) + "-" + ('0' + this.currentDate.getDate()).slice(-2);;
    }
    else
      this.reportSearch.issueDateFrom = this.issueDateFromSearch;

    if (this.issueDateToSearch == undefined) {
      this.reportSearch.issueDateTo = "9999-99-99";
    }
    else
      this.reportSearch.issueDateTo = this.issueDateToSearch;
    this.reportSearch.dealerId = this.certificateReportForm.get('dealerId').value;
    this.totalCredits = 0;
    this.totalDebits = 0;
    this.searchService.getPaymentReport(this.reportSearch)
      .pipe(first())
      .subscribe({
        next: (x) => {
          var runningBalance = parseFloat(x[0].runningBalance);
          x = x.map((y) => {
            y.runningBalance = parseFloat(y.runningBalance).toFixed(2)
            runningBalance = runningBalance + y.credit - y.debit ? y.debit : 0;
            y.runningBalanceCalculated = runningBalance;
            return y;
          });
          this.aggrid.api.setRowData(x);
          this.loading = false;
        },
        error: error => {
          this.alertService.error(error)
          this.loading = false;
        }
      })

  }

  onReset() {
    this.reportCreated = false;
    this.submitted = false;
    this.loading = false;
  }

  exportToExcel() {
    const params = {
      columnGroups: true,
      allColumns: true,
      fileName: 'excelReport',
    };
    this.aggrid.api.exportDataAsCsv(params);
  }

}
